/**
 * Dashboard Page - React migration of AngularJS dashboard
 * Issue #77: Phase 1 - Foundation Architecture
 */

import { useEffect, useState } from 'react';
import { Button, Spin, Alert, Row, Col } from 'antd';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { getCurrentTenant } from '../../config/tenant';
import {
  fetchDashboardInfo,
  fetchChartData,
  fetchScorecards,
  fetchMedicalDirectorDashboard,
  setFacilityFromContext,
  setFilters,
  showBroadcastModal,
  showLabsModal,
  setDashboardType
} from '../../store/dashboardSlice';
import type { ChartClickData } from '../../pages/dashboard/utils/chartDataTransform';

// Dashboard components
import FacilityFilter from '../../pages/dashboard/components/FacilityFilter';
import DateRangeFilter from '../../pages/dashboard/components/DateRangeFilter';
import DateRangePresetSelector from '../../pages/dashboard/components/DateRangePresetSelector';
import ExportButton from '../../pages/dashboard/components/ExportButton';
import DashboardTypeSelector from '../../pages/dashboard/components/DashboardTypeSelector';
import MetricsSummaryTable from '../../pages/dashboard/components/MetricsSummaryTable';
import EncounterChart from '../../pages/dashboard/components/EncounterChart';
import Scorecards from '../../pages/dashboard/components/Scorecards';
import BroadcastMessageModal from '../../pages/dashboard/components/BroadcastMessageModal';
import OverdueLabsModal from '../../pages/dashboard/components/OverdueLabsModal';
import MedicalDirectorDashboard from '../../pages/dashboard/components/MedicalDirectorDashboard';
import MasterOperationsDashboard from '../../pages/dashboard/components/MasterOperationsDashboard';
import WatchProvidersModal from '../../pages/dashboard/components/WatchProvidersModal';
import QualityDashboard from '../../pages/dashboard/components/QualityDashboard';
import dashboardApi from '../../pages/dashboard/services/dashboardApi';

function DashboardPage() {
  const dispatch = useAppDispatch();
  const {
    info,
    filters,
    loading,
    error,
    medicalDirectorData
  } = useAppSelector(state => state.dashboard);

  // Get facilities from shared Redux state (not from dashboard.info)
  const { facilities, selectedFacilityId } = useAppSelector(state => state.facilities);

  const [watchProvidersModalOpen, setWatchProvidersModalOpen] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const authContext = (window as any).authContext;
  const permissions: string[] = authContext?.employee?.permissions || authContext?.permissions || [];
  const isSuperuser = Boolean(authContext?.user?.is_superuser);
  const canViewStandardDashboards = isSuperuser || permissions.includes('dashboard');
  const canViewQualityDashboard = isSuperuser || permissions.includes('quality_dashboard');
  const masterSchemaName = ((window as any)?.tenantContext?.schema ||
    (window as any)?.tenantContext?.schema_name ||
    getCurrentTenant()?.schema);
  const isOpsHost = window.location.hostname === 'ops.localhost' || window.location.hostname.startsWith('ops.');
  const isMasterDashboardSchema = isOpsHost || masterSchemaName === 'tec_base';

  // Initialize dashboard on mount
  useEffect(() => {
    if (isMasterDashboardSchema) {
      return;
    }

    // Immediately sync facility from tenant context BEFORE fetching data
    // This ensures the login facility is used for the initial chart data request
    const tenantContext = (window as any)?.tenantContext;
    if (tenantContext?.facilityId) {
      const facilityId = parseInt(tenantContext.facilityId);
      if (!isNaN(facilityId)) {
        dispatch(setFacilityFromContext(facilityId));
        dispatch(setFilters({ facilityId }));
      }
    }

    dispatch(fetchDashboardInfo());

    // Handle hash-based dashboard routing (matches AngularJS implementation)
    const hash = window.location.hash;
    if (hash === '#/quality' && canViewQualityDashboard) {
      dispatch(setDashboardType('quality'));
    } else if (hash === '#/med_dash' && canViewStandardDashboards) {
      dispatch(setDashboardType('med_dash'));
    } else if (hash === '#/info' && canViewStandardDashboards) {
      dispatch(setDashboardType('info'));
    } else if (hash && hash !== '#/' && hash !== '#') {
      setAccessError('You do not have permission to view the requested dashboard.');
      dispatch(setDashboardType(canViewQualityDashboard ? 'quality' : 'info'));
    } else if (canViewQualityDashboard && !canViewStandardDashboards) {
      dispatch(setDashboardType('quality'));
    } else {
      // Default to Patient Counts if no hash specified
      dispatch(setDashboardType('info'));
    }
  }, [
    dispatch,
    isMasterDashboardSchema,
    canViewQualityDashboard,
    canViewStandardDashboards,
  ]);

  // Sync facility selection from facilities slice to dashboard slice
  // This handles cases where user changes facility via FacilityFilter dropdown
  useEffect(() => {
    if (isMasterDashboardSchema) {
      return;
    }

    if (selectedFacilityId !== null && selectedFacilityId !== filters.facilityId) {
      dispatch(setFacilityFromContext(selectedFacilityId));
      dispatch(setFilters({ facilityId: selectedFacilityId }));
    }
  }, [selectedFacilityId, filters.facilityId, dispatch, isMasterDashboardSchema]);

  // Auto-search when dashboard info loads and facility is selected (including "All Facilities" which is null)
  useEffect(() => {
    if (isMasterDashboardSchema) {
      return;
    }

    if (info && filters.facilityId !== undefined) {
      // Auto-search with current filters based on dashboard type
      // facilityId can be null (All Facilities) or a number (specific facility)
      if (filters.dashboardType === 'info') {
        dispatch(fetchChartData(filters));
        dispatch(fetchScorecards(filters));
      } else if (filters.dashboardType === 'med_dash') {
        dispatch(fetchMedicalDirectorDashboard({ filters }));
      }
    }
  }, [dispatch, info, filters.facilityId, filters.dashboardType, isMasterDashboardSchema]);

  // Auto-refresh Medical Director dashboard when date range changes
  useEffect(() => {
    if (isMasterDashboardSchema) {
      return;
    }

    if (
      info &&
      filters.facilityId !== undefined &&
      filters.dashboardType === 'med_dash'
    ) {
      dispatch(fetchMedicalDirectorDashboard({ filters }));
    }
  }, [dispatch, info, filters.facilityId, filters.dashboardType, filters.dateRange, isMasterDashboardSchema]);

  // Facility selection is now handled by the facilities slice

  // Show modals when dashboard info loads (mimicking AngularJS behavior)
  useEffect(() => {
    if (isMasterDashboardSchema || filters.dashboardType === 'quality') {
      return;
    }

    if (info) {
      // Show broadcast messages if there are unread ones
      const unreadMessages = info.broadcast_messages?.filter(msg => !msg.has_user_seen) || [];
      if (unreadMessages.length > 0) {
        // Small delay to let the dashboard load first
        setTimeout(() => dispatch(showBroadcastModal()), 500);
      }
      
      // Show overdue labs alert if there are any (but only once per session)
      if (info.overdue_labs && info.overdue_labs.lab_count > 0) {
        const labsModalShown = sessionStorage.getItem('overdueLabsModalShown');
        if (!labsModalShown) {
          // Mark as shown for this session
          sessionStorage.setItem('overdueLabsModalShown', 'true');
          // Show after broadcast messages (if any), otherwise show immediately
          const delay = unreadMessages.length > 0 ? 1000 : 500;
          setTimeout(() => dispatch(showLabsModal()), delay);
        }
      }
    }
  }, [dispatch, info, filters.dashboardType, isMasterDashboardSchema]);

  if (isMasterDashboardSchema) {
    return <MasterOperationsDashboard />;
  }

  if (!canViewStandardDashboards && !canViewQualityDashboard) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="Dashboard Access Denied"
          description="Your account does not have permission to view a dashboard."
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Handle search button click
  const handleSearch = () => {
    if (filters.dashboardType === 'info') {
      dispatch(fetchChartData(filters));
      dispatch(fetchScorecards(filters));
    } else if (filters.dashboardType === 'med_dash') {
      dispatch(fetchMedicalDirectorDashboard({ filters }));
    }
  };


  // Handle Watch Providers modal
  const handleWatchProvidersModalClose = () => {
    setWatchProvidersModalOpen(false);
    // Refresh Medical Director dashboard after modal close
    if (filters.dashboardType === 'med_dash') {
      dispatch(fetchMedicalDirectorDashboard({ filters }));
    }
  };

  // Handle watch provider toggle
  const handleToggleWatch = async (providerId: number) => {
    try {
      if (!medicalDirectorData) return;

      // Find the provider and calculate new watched list
      const providers = medicalDirectorData.providers || [];
      const provider = providers.find((p: any) => p.id === providerId);
      if (!provider) return;

      const currentWatchedIds = providers
        .filter((p: any) => p.is_watched)
        .map((p: any) => p.id);
      
      const newWatchedIds = provider.is_watched 
        ? currentWatchedIds.filter(id => id !== providerId)
        : [...currentWatchedIds, providerId];

      // Update the backend
      await dashboardApi.updateWatches(newWatchedIds);
      
    } catch (error) {
      console.error('Failed to update provider watch status:', error);
    }
  };

  // Handle chart click navigation to React patients page
  const handleChartClick = (data: ChartClickData) => {
    // Build URL for React patients page with search params
    const params = new URLSearchParams();

    // Set tab to encounters (All Encounters tab)
    params.set('tab', 'encounters');

    // Add period date range (specific to the clicked bar)
    // Convert ISO dates to YYYY-MM-DD format for the date inputs
    params.set('start_date', data.periodStart.slice(0, 10));
    params.set('end_date', data.periodEnd.slice(0, 10));

    // Add facility filter if available
    if (data.facilityId) {
      params.set('facility', data.facilityId.toString());
    }

    // Navigate to React patients page with query params
    window.location.href = `/patients?${params.toString()}`;
  };

  if (loading.info) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spin size="large" tip="Loading dashboard..." />
      </div>
    );
  }

  if (error.info) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="Dashboard Error"
          description={error.info}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (!info) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="No Dashboard Data"
          description="Unable to load dashboard configuration."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ margin: '20px' }}>
      {accessError && (
        <Alert
          closable
          showIcon
          type="warning"
          message="Requested dashboard unavailable"
          description={accessError}
          onClose={() => setAccessError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {/* Dashboard Header with Filters */}
      <div className="header">
        <div className="filter-container">
          {/* Filters and Buttons on Same Line - Right Justified */}
          <Row gutter={[16, 16]} align="bottom" justify="end" style={{ marginBottom: '20px' }}>
            <Col>
              <DashboardTypeSelector />
            </Col>
            {/* Show predefined date range dropdown only for Medical Director Dashboard */}
            {filters.dashboardType === 'med_dash' && (
              <Col>
                <DateRangePresetSelector />
              </Col>
            )}
            <Col>
              <DateRangeFilter />
            </Col>
            
            {/* Patient Counts Dashboard Filters */}
            {filters.dashboardType === 'info' && (
              <>
                <Col>
                  <FacilityFilter facilities={facilities} />
                </Col>
                <Col>
                  <Button
                    type="primary"
                    danger
                    onClick={handleSearch}
                    loading={loading.chartData || loading.scorecards}
                    className="tec-button-md tec-button-red"
                    size="large"
                  >
                    Search
                  </Button>
                </Col>
                <Col>
                  <ExportButton />
                </Col>
              </>
            )}

            {/* Medical Director Dashboard Controls */}
            {filters.dashboardType === 'med_dash' && (
              <Col>
                <Button
                  type="primary"
                  onClick={() => setWatchProvidersModalOpen(true)}
                  size="large"
                >
                  Watch Providers
                </Button>
              </Col>
            )}
          </Row>

          {/* Metrics Summary Table - Only show for Patient Counts dashboard */}
          {filters.dashboardType === 'info' && <MetricsSummaryTable />}
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="content">
        {/* Patient Counts Dashboard (info) */}
        {filters.dashboardType === 'info' && (
          <>
            <EncounterChart onChartClick={handleChartClick} />
            {/* Scorecards display */}
            <Scorecards className="scorecards-container" />
          </>
        )}

        {/* Medical Director Dashboard (med_dash) */}
        {filters.dashboardType === 'med_dash' && (
          <div className="medical-director-dashboard">
            {loading.medicalDirector ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <Spin size="large" tip="Loading Medical Director Dashboard..." />
              </div>
            ) : error.medicalDirector ? (
              <div style={{ padding: '20px' }}>
                <Alert
                  message="Medical Director Dashboard Error"
                  description={error.medicalDirector}
                  type="error"
                  showIcon
                />
              </div>
            ) : medicalDirectorData ? (
              <MedicalDirectorDashboard 
                data={medicalDirectorData} 
                loading={loading.medicalDirector}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <Alert 
                  message="No Data Available"
                  description="Please select filters and click Search to load Medical Director dashboard data."
                  type="info"
                  showIcon
                />
                <div style={{ marginTop: '20px' }}>
                  <Button 
                    type="link" 
                    onClick={() => window.location.href = '/dashboard/#/info'}
                  >
                    Switch to Patient Counts Dashboard
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {filters.dashboardType === 'quality' && canViewQualityDashboard && (
          <QualityDashboard dateRange={filters.dateRange} />
        )}
      </div>

      {/* Modals */}
      {filters.dashboardType !== 'quality' && (
        <>
          <BroadcastMessageModal />
          <OverdueLabsModal />
        </>
      )}
      
      {/* Watch Providers Modal - Only show for Medical Director dashboard */}
      {filters.dashboardType === 'med_dash' && medicalDirectorData && (
        <WatchProvidersModal
          open={watchProvidersModalOpen}
          onClose={handleWatchProvidersModalClose}
          providers={medicalDirectorData.providers || []}
          facilities={info?.facilities || []}
          onToggleWatch={handleToggleWatch}
        />
      )}

    </div>
  );
}

export default DashboardPage;
